import axios, { AxiosInstance } from 'axios';
import toast from 'react-hot-toast';

// Version: 2.0.1 - Heroku deployment
// Use relative URL in production, absolute in development
const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (import.meta.env.DEV ? 'http://localhost:3001/api' : '/api');

console.log('[AnonLink] API URL configured as:', API_BASE_URL);
console.log('[AnonLink] Build version: 2.0.0');

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          // Handle specific error codes
          switch (error.response.status) {
            case 401:
              // Unauthorized - clear auth but don't redirect
              // Let the app handle redirects
              localStorage.removeItem('authToken');
              break;
            case 429:
              toast.error('Too many requests. Please slow down.');
              break;
            case 500:
              toast.error('Server error. Please try again later.');
              break;
          }
        } else if (error.request) {
          toast.error('Network error. Please check your connection.');
        }
        
        return Promise.reject(error);
      }
    );
  }

  // URL endpoints
  async createShortUrl(data: {
    url: string;
    customAlias?: string;
    expiresIn?: string;
    password?: string;
    maxClicks?: number;
    burnAfterReading?: boolean;
  }) {
    return this.client.post('/urls/shorten', data);
  }

  async getUrl(hash: string) {
    return this.client.get(`/urls/${hash}`);
  }

  async verifyPassword(data: { hash: string; password: string }) {
    return this.client.post('/urls/verify-password', data);
  }

  async deleteUrl(hash: string) {
    return this.client.delete(`/urls/${hash}`);
  }

  async getMyUrls() {
    return this.client.get('/urls/my/list');
  }

  // Analytics endpoints
  async getAnalytics(hash: string) {
    return this.client.get(`/analytics/${hash}`);
  }

  // Health check
  async healthCheck() {
    return this.client.get('/health');
  }

  // Authentication endpoints
  async register(data: {
    email: string;
    password: string;
    username?: string;
    displayName?: string;
  }) {
    return this.client.post('/auth/register', data);
  }

  async login(data: { email: string; password: string }) {
    return this.client.post('/auth/login', data);
  }

  async logout() {
    return this.client.post('/auth/logout');
  }

  async getCurrentUser() {
    return this.client.get('/auth/me');
  }

  async initGuestSession() {
    return this.client.post('/auth/guest/init');
  }

  async convertGuestToUser() {
    return this.client.post('/auth/guest/convert');
  }

  // WebAuthn endpoints
  async getWebAuthnRegistrationOptions(data: {
    email: string;
    username?: string;
  }) {
    return this.client.post('/auth/webauthn/register-options', data);
  }

  async verifyWebAuthnRegistration(response: any) {
    return this.client.post('/auth/webauthn/register-verify', response);
  }

  async getWebAuthnLoginOptions(email?: string) {
    return this.client.post('/auth/webauthn/login-options', { email });
  }

  async verifyWebAuthnLogin(response: any) {
    return this.client.post('/auth/webauthn/login-verify', response);
  }

  async getWebAuthnCredentials() {
    return this.client.get('/auth/webauthn/credentials');
  }

  async deleteWebAuthnCredential(credentialId: string) {
    return this.client.delete(`/auth/webauthn/credentials/${credentialId}`);
  }

  // Password reset
  async requestPasswordReset(email: string) {
    return this.client.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string) {
    return this.client.post('/auth/reset-password', { token, password });
  }

  async verifyEmail(token: string) {
    return this.client.get(`/auth/verify-email/${token}`);
  }

  // Billing endpoints
  async createCheckoutSession(data: {
    successUrl: string;
    cancelUrl: string;
  }) {
    return this.client.post('/billing/create-checkout', data);
  }

  async createBillingPortalSession(returnUrl: string) {
    return this.client.post('/billing/portal', { returnUrl });
  }

  async cancelSubscription() {
    return this.client.post('/billing/cancel-subscription');
  }

  async getSubscriptionStatus() {
    return this.client.get('/billing/subscription');
  }

  // Helper methods for API
  get(path: string) {
    return this.client.get(path);
  }

  post(path: string, data?: any) {
    return this.client.post(path, data);
  }

  delete(path: string) {
    return this.client.delete(path);
  }
}

export default new ApiService();